import * as React from 'react'
import { cn } from '@/shared/lib/utils'

function Card({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot='card'
      className={cn(
        'flex flex-col gap-2 rounded-xl border border-border bg-card py-4 text-card-foreground',
        className
      )}
      {...props}
    />
  )
}

function CardHeader({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot='card-header'
      className={cn(
        '@container/card-header grid auto-rows-min grid-rows-[auto_auto] items-start gap-2 px-4 only:pb-0 has-data-[slot=card-action]:grid-cols-[1fr_auto] has-[+[data-slot=collapsible-content][data-closed]]:pb-0! [.border-b]:pb-4',
        className
      )}
      {...props}
    />
  )
}

function CardTitle({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot='card-title'
      className={cn('leading-none font-semibold', className)}
      {...props}
    />
  )
}

function CardDescription({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot='card-description'
      className={cn('text-sm text-muted-foreground', className)}
      {...props}
    />
  )
}

function CardAction({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot='card-action'
      className={cn(
        'col-start-2 row-span-2 row-start-1 self-start justify-self-end',
        className
      )}
      {...props}
    />
  )
}

function CardContent({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot='card-content'
      className={cn('px-4', className)}
      {...props}
    />
  )
}

function CardFooter({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot='card-footer'
      className={cn('flex items-center px-4 [.border-t]:pt-4', className)}
      {...props}
    />
  )
}

type StatCardProps =
  | {
      label: string
      value: number
      icon?: React.ReactNode
      description?: string
      className?: string
      percent?: never
      progressPercent?: never
    }
  | {
      label: string
      percent: number
      progressPercent?: number
      showProgressBar?: boolean
      icon?: React.ReactNode
      description?: string
      className?: string
      value?: never
    }

function StatCard(props: StatCardProps) {
  const { label, icon, description, className } = props
  const showPercent = props.percent != null
  const showProgressBar =
    showPercent && 'showProgressBar' in props
      ? (props.showProgressBar ?? true)
      : true
  const progressPercent =
    showPercent && showProgressBar
      ? (props.progressPercent ?? props.percent)
      : null

  return (
    <Card className={cn('h-full', className)}>
      <CardHeader className='flex flex-row items-center justify-between space-y-0 pb-2'>
        <CardTitle className='text-sm font-medium'>{label}</CardTitle>
        {icon && <div className='text-muted-foreground'>{icon}</div>}
      </CardHeader>
      <CardContent className='mt-auto space-y-2'>
        {showPercent && (
          <div className='text-2xl font-bold'>{props.percent}%</div>
        )}
        {!showPercent && (
          <div className='text-2xl font-bold'>
            {props.value.toLocaleString()}
          </div>
        )}
        {progressPercent != null && (
          <div
            className='h-2 w-full overflow-hidden rounded-full bg-muted'
            role='progressbar'
            aria-valuemin={0}
            aria-valuemax={100}
            aria-valuenow={progressPercent}
            aria-label={label}
          >
            <div
              className='h-full rounded-full bg-primary transition-all'
              style={{ width: `${progressPercent}%` }}
            />
          </div>
        )}
        {description && (
          <p className='text-xs text-muted-foreground'>{description}</p>
        )}
      </CardContent>
    </Card>
  )
}

export {
  Card,
  CardHeader,
  CardFooter,
  CardTitle,
  CardAction,
  CardDescription,
  CardContent,
  StatCard,
}
